import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEstablishment } from '@/contexts/EstablishmentContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { PiktableLoader } from '@/components/PiktableLoader';

import { OnboardingLayout } from '@/components/onboarding/OnboardingLayout';
import { StepZones } from '@/components/onboarding/StepZones';
import { StepTables } from '@/components/onboarding/StepTables';
import { StepTimeSlots } from '@/components/onboarding/StepTimeSlots';
import { StepBookingMode } from '@/components/onboarding/StepBookingMode';
import { StepNotifications } from '@/components/onboarding/StepNotifications';
import { StepComplete } from '@/components/onboarding/StepComplete';
import { SkipAllDialog } from '@/components/onboarding/SkipAllDialog';

const STEP_TITLES = [
  'Zones',
  'Tables',
  'Créneaux horaires',
  'Mode de réservation',
  'Notifications',
  'Terminé'
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentEstablishment, loading: establishmentLoading, refetch } = useEstablishment();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showSkipAllDialog, setShowSkipAllDialog] = useState(false);

  const handleSkipAll = () => {
    setShowSkipAllDialog(true);
  };

  const handleConfirmSkipAll = () => {
    setCurrentStep(6); // Jump to final step
  };

  // Collected data
  const [zonesData, setZonesData] = useState<{
    zones: Array<{ name: string; color: string }>;
  } | null>(null);

  const [tablesData, setTablesData] = useState<{
    tables: Array<{ name: string; capacity: number; zoneName?: string }>;
  } | null>(null);
  
  const [timeSlotsData, setTimeSlotsData] = useState<{
    slots: Array<{ startTime: string; endTime: string; days: number[] }>;
  } | null>(null);

  const [bookingModeData, setBookingModeData] = useState<{
    bookingMode: 'slot' | 'time';
    bookingIntervalMinutes: 15 | 30 | 60;
    reservationDurationMinutes: number;
  } | null>(null);
  
  const [notificationsData, setNotificationsData] = useState<{
    newReservation: boolean;
    dailySummary: boolean;
    sendTime: string;
    recipientEmail: string;
  } | null>(null);

  // Redirect if no establishment or already onboarded
  useEffect(() => {
    if (!establishmentLoading && currentEstablishment) {
      if (currentEstablishment.onboarding_completed) {
        navigate(`/${currentEstablishment.slug}/dashboard`);
      }
    }
  }, [currentEstablishment, establishmentLoading, navigate]);

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleZonesComplete = (data: { zones: Array<{ name: string; color: string }> }) => {
    setZonesData(data);
    handleNext();
  };

  const handleTablesComplete = (data: { tables: Array<{ name: string; capacity: number; zoneName?: string }> }) => {
    setTablesData(data);
    handleNext();
  };

  const handleTimeSlotsComplete = (data: { slots: Array<{ startTime: string; endTime: string; days: number[] }> }) => {
    setTimeSlotsData(data);
    handleNext();
  };

  const handleBookingModeComplete = (data: {
    bookingMode: 'slot' | 'time';
    bookingIntervalMinutes: 15 | 30 | 60;
    reservationDurationMinutes: number;
  }) => {
    setBookingModeData(data);
    handleNext();
  };

  const handleNotificationsComplete = (data: {
    newReservation: boolean;
    dailySummary: boolean;
    sendTime: string;
    recipientEmail: string;
  }) => {
    setNotificationsData(data);
    handleNext();
  };

  const handleFinish = async () => {
    if (!currentEstablishment) return;
    
    setSaving(true);
    
    try {
      // Map to store zone name -> zone id
      const zoneIdMap: Record<string, string> = {};

      // 1. Create zones first
      if (zonesData && zonesData.zones.length > 0) {
        const zonesToInsert = zonesData.zones.map((z, index) => ({
          establishment_id: currentEstablishment.id,
          name: z.name,
          color: z.color,
          order_index: index,
          is_default: index === 0,
        }));

        const { data: createdZones, error: zonesError } = await supabase
          .from('floor_plan_zones')
          .insert(zonesToInsert)
          .select('id, name');

        if (zonesError) {
          console.error('Error creating zones:', zonesError);
          throw zonesError;
        }

        // Build zone name to ID mapping
        if (createdZones) {
          createdZones.forEach(zone => {
            zoneIdMap[zone.name] = zone.id;
          });
        }
      }

      // 2. Create tables
      if (tablesData && tablesData.tables.length > 0) {
        const tablesToInsert = tablesData.tables.map(t => ({
          establishment_id: currentEstablishment.id,
          name: t.name,
          capacity: t.capacity,
          is_active: true,
          is_bookable: true,
        }));

        const { data: createdTables, error: tablesError } = await supabase
          .from('tables')
          .insert(tablesToInsert)
          .select('id, name');

        if (tablesError) {
          console.error('Error creating tables:', tablesError);
          throw tablesError;
        }

        // 3. Create floor plan positions for tables with zones
        if (createdTables) {
          // Use grid units (not pixels!) - matches FloorPlanView grid system
          const tableSize = 1; // Small 1x1 squares
          const spacing = 2; // Generous spacing
          const gridCols = 12;
          const gridRows = 8;
          const maxTablesPerRow = Math.min(5, Math.floor(gridCols / (tableSize + spacing)));
          const totalTables = createdTables.length;
          const totalRows = Math.ceil(totalTables / maxTablesPerRow);
          
          // Calculate grid dimensions for centering
          const tablesPerRow = Math.min(totalTables, maxTablesPerRow);
          const gridWidth = tablesPerRow * (tableSize + spacing) - spacing;
          const gridHeight = totalRows * (tableSize + spacing) - spacing;
          
          // Center in the visible area
          const startX = Math.max(1, Math.floor((gridCols - gridWidth) / 2));
          const startY = Math.max(1, Math.floor((gridRows - gridHeight) / 2));
          
          const positionsToInsert = createdTables.map((table, index) => {
            const originalTable = tablesData.tables.find(t => t.name === table.name);
            const zoneId = originalTable?.zoneName ? zoneIdMap[originalTable.zoneName] : null;
            
            const row = Math.floor(index / maxTablesPerRow);
            const col = index % maxTablesPerRow;
            
            // Center incomplete last rows
            const tablesInThisRow = Math.min(maxTablesPerRow, totalTables - row * maxTablesPerRow);
            const rowOffset = row === totalRows - 1 && tablesInThisRow < maxTablesPerRow
              ? Math.floor((maxTablesPerRow - tablesInThisRow) * (tableSize + spacing) / 2)
              : 0;
            
            return {
              establishment_id: currentEstablishment.id,
              table_id: table.id,
              zone_id: zoneId,
              x: startX + col * (tableSize + spacing) + rowOffset,
              y: startY + row * (tableSize + spacing),
              width: tableSize,
              height: tableSize,
            };
          });

          const { error: positionsError } = await supabase
            .from('floor_plan_positions')
            .insert(positionsToInsert);

          if (positionsError) {
            console.error('Error creating positions:', positionsError);
            // Non-blocking error - positions can be set later
          }
        }
      }

      // 4. Create time slots
      if (timeSlotsData && timeSlotsData.slots.length > 0) {
        const slotsToInsert = timeSlotsData.slots.map(s => ({
          establishment_id: currentEstablishment.id,
          start_time: s.startTime,
          end_time: s.endTime,
          available_days: s.days,
          is_active: true,
        }));

        const { error: slotsError } = await supabase
          .from('time_slots')
          .insert(slotsToInsert);

        if (slotsError) {
          console.error('Error creating time slots:', slotsError);
          throw slotsError;
        }
      }

      // 5. Update booking mode settings
      if (bookingModeData) {
        const { error: bookingModeError } = await supabase
          .from('reservation_settings')
          .update({
            booking_mode: bookingModeData.bookingMode,
            booking_interval_minutes: bookingModeData.bookingIntervalMinutes,
            reservation_duration_minutes: bookingModeData.reservationDurationMinutes,
          })
          .eq('establishment_id', currentEstablishment.id);

        if (bookingModeError) {
          console.error('Error updating booking mode:', bookingModeError);
          // Non-blocking error - can be configured later
        }
      }

      // 6. Update notification settings
      if (notificationsData) {
        // Update new reservation notification
        await supabase
          .from('notification_settings')
          .update({
            is_enabled: notificationsData.newReservation,
            recipient_email: notificationsData.recipientEmail,
          })
          .eq('establishment_id', currentEstablishment.id)
          .eq('setting_key', 'new_reservation');

        // Update daily summary notification
        await supabase
          .from('notification_settings')
          .update({
            is_enabled: notificationsData.dailySummary,
            recipient_email: notificationsData.recipientEmail,
            send_time: notificationsData.sendTime + ':00',
          })
          .eq('establishment_id', currentEstablishment.id)
          .eq('setting_key', 'daily_summary_email');
      }

      // 7. Mark onboarding as complete
      const { error: updateError } = await supabase
        .from('establishments')
        .update({ onboarding_completed: true })
        .eq('id', currentEstablishment.id);

      if (updateError) {
        console.error('Error updating onboarding status:', updateError);
        throw updateError;
      }

      toast({
        title: "Configuration terminée !",
        description: "Votre établissement est prêt à recevoir des réservations.",
      });

      // Refetch establishment and navigate to dashboard
      await refetch();
      navigate(`/${currentEstablishment.slug}/dashboard`);
    } catch (error: any) {
      console.error('Onboarding error:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la configuration.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (establishmentLoading) {
    return <PiktableLoader message="Préparation de l'onboarding..." />;
  }

  if (!currentEstablishment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Établissement non trouvé</p>
      </div>
    );
  }

  return (
    <>
      <SkipAllDialog
        open={showSkipAllDialog}
        onOpenChange={setShowSkipAllDialog}
        onConfirmSkip={handleConfirmSkipAll}
      />
      
      <OnboardingLayout
        currentStep={currentStep}
        totalSteps={6}
        stepTitles={STEP_TITLES}
        establishmentName={currentEstablishment.name}
        onSkipAll={handleSkipAll}
        showSkipAll={true}
      >
        {currentStep === 1 && (
        <StepZones
          onComplete={handleZonesComplete}
          onSkip={handleSkip}
        />
      )}
      {currentStep === 2 && (
        <StepTables
          zones={zonesData?.zones || []}
          onComplete={handleTablesComplete}
          onBack={handleBack}
          onSkip={handleSkip}
        />
      )}
      {currentStep === 3 && (
        <StepTimeSlots
          onComplete={handleTimeSlotsComplete}
          onBack={handleBack}
          onSkip={handleSkip}
        />
      )}
      {currentStep === 4 && (
        <StepBookingMode
          onComplete={handleBookingModeComplete}
          onBack={handleBack}
          onSkip={handleSkip}
        />
      )}
      {currentStep === 5 && (
        <StepNotifications
          defaultEmail={currentEstablishment.email || ''}
          onComplete={handleNotificationsComplete}
          onBack={handleBack}
          onSkip={handleSkip}
        />
      )}
      {currentStep === 6 && (
        <StepComplete
          establishmentSlug={currentEstablishment.slug}
          zonesCount={zonesData?.zones.length || 0}
          tablesCount={tablesData?.tables.length || 0}
          timeSlotsCount={timeSlotsData?.slots.length || 0}
          notificationsEnabled={notificationsData?.newReservation || notificationsData?.dailySummary || false}
          bookingMode={bookingModeData?.bookingMode}
          bookingIntervalMinutes={bookingModeData?.bookingIntervalMinutes}
          reservationDurationMinutes={bookingModeData?.reservationDurationMinutes}
          onFinish={handleFinish}
          onGoToFloorPlan={async () => {
            await handleFinish();
            navigate('/dashboard?tab=salle');
          }}
          onBack={handleBack}
          saving={saving}
        />
        )}
      </OnboardingLayout>
    </>
  );
}
